"use client";

import { createContext, useEffect, useState, ReactNode } from "react";

interface ThemeContextType {
    theme: string;
    changeTheme: (newTheme: string) => void;
}

export const ThemeContext = createContext<ThemeContextType>({
    theme: "light",
    changeTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const [theme, setTheme] = useState<string>("system");
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        // Check for saved theme
        const storedTheme = localStorage.getItem("theme");
        if (storedTheme) {
            setTheme(storedTheme);
        } else {
            // Use system preference
            const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
            setTheme(prefersDark ? "dark" : "light");
        }
        setIsMounted(true);
    }, []);

    // Update localStorage and html attribute when theme changes
    useEffect(() => {
        if (isMounted) {
            localStorage.setItem("theme", theme);
            document.documentElement.setAttribute("data-theme", theme);
        }
    }, [theme, isMounted]);

    const changeTheme = (newTheme: string) => {
        setTheme(newTheme);
    };

    if (!isMounted) {
        return <>Loading...</>;
    }

    return (
        <ThemeContext.Provider value={{ theme, changeTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
